/**
 * ChartTooltip — shared themed tooltip for every Recharts chart on the v2
 * dashboard (bug F1).
 *
 * Visual contract — same class set `TimeSavedChart` shipped first:
 *   `rounded-md border bg-popover px-2.5 py-1.5 text-xs
 *    text-popover-foreground shadow-md`
 *
 * Problem it solves: Recharts' default tooltip renders as bright-white panel
 * with black text. On the shadcn dark theme that looks like a rectangle of
 * white-on-white; even on the light theme its border/shadow don't match
 * the rest of the UI. Users explicitly complained about "хреновый стиль
 * ховер сообщения" — TimeSavedChart already had the right pattern, we just
 * lacked a single source of truth so every author reinvented it (or
 * skipped it).
 *
 * Usage:
 *   import { ChartTooltip } from '@/components/charts/ChartTooltip';
 *   <Tooltip content={<ChartTooltip />} />               // default rendering
 *   <Tooltip content={<ChartTooltip labelFormatter={fmt} />} />
 *   <Tooltip content={<ChartTooltip valueFormatter={(v, name) => `${v} req`} />} />
 *
 * For more exotic layouts (per-series colour dot, extra metadata rows) use
 * the exported `TOOLTIP_CLASSNAME` / `TOOLTIP_VALUE_CLASSNAME` constants
 * on a custom `<Tooltip content={...} />` renderer so the visual stays in
 * lock-step with the default.
 */

import * as React from 'react';
import type { TooltipProps } from 'recharts';

/** Surface-level class set — border + popover bg + subtle shadow. */
export const TOOLTIP_CLASSNAME =
  'rounded-md border bg-popover px-2.5 py-1.5 text-xs text-popover-foreground shadow-md';

/**
 * Touch-only pointer detection — SSOT for "hover is unavailable" queries.
 *
 * Signals combined as a union:
 *   1. `matchMedia('(hover: none)')` — primary pointer can't hover. True on
 *      iOS Safari / Android Chrome on real devices. Set correctly by Chrome
 *      device-mode ONLY if the user enables the "Rendering → Emulate CSS
 *      media feature: hover" panel — otherwise DevTools leaves it `hover`
 *      even while `hasTouch` is on. This is why we can't rely on it alone.
 *   2. `matchMedia('(pointer: coarse)')` — primary pointer is imprecise.
 *      Mirrors (hover: none) on phones; some environments flip one but not
 *      the other.
 *   3. `navigator.maxTouchPoints > 0` — the device (or emulator) reports a
 *      touch digitizer. Catches Chrome DevTools device-mode where `hasTouch`
 *      is on but neither media query flips.
 *   4. `'ontouchstart' in window` — legacy capability flag, still true on
 *      old Android WebViews that don't expose `maxTouchPoints`.
 *
 * We treat any of these as "no reliable hover". Re-evaluates on media
 * change (hybrid laptop-tablets flip (hover: none) when the keyboard
 * detaches).
 *
 * Used for three v1.4 affordances:
 *   1. Tooltip trigger — `useChartTooltipTrigger()` below switches from
 *      `hover` to `click` on touch so the tooltip doesn't flash-and-vanish
 *      on tap.
 *   2. Persistent bar outline — charts paint a subtle foreground stroke on
 *      every clickable bar when touch-only, because the Recharts `activeBar`
 *      hover outline never fires without a mouse, leaving mobile users with
 *      no cue that bars are tappable.
 *   3. `InteractiveChartHint` — "Tap to drilldown" vs "Click to drilldown" text
 *      so the instruction matches the user's actual input mode.
 */
export function useIsTouchOnly(): boolean {
  const [touchOnly, setTouchOnly] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (typeof window === 'undefined') return;

    const compute = (): boolean => {
      if (typeof window.matchMedia === 'function') {
        if (window.matchMedia('(hover: none)').matches) return true;
        if (window.matchMedia('(pointer: coarse)').matches) return true;
      }
      const mtp = typeof navigator !== 'undefined' ? navigator.maxTouchPoints : 0;
      if (typeof mtp === 'number' && mtp > 0) return true;
      if ('ontouchstart' in window) return true;
      return false;
    };

    const apply = (): void => setTouchOnly(compute());
    apply();

    // Subscribe to both media queries so a hybrid laptop-tablet's keyboard
    // detach / re-attach flips the value live. maxTouchPoints / ontouchstart
    // aren't reactive, but they're stable per-session so one-shot check is OK.
    if (typeof window.matchMedia !== 'function') return;
    const mqHover = window.matchMedia('(hover: none)');
    const mqPointer = window.matchMedia('(pointer: coarse)');
    mqHover.addEventListener('change', apply);
    mqPointer.addEventListener('change', apply);
    return () => {
      mqHover.removeEventListener('change', apply);
      mqPointer.removeEventListener('change', apply);
    };
  }, []);

  return touchOnly;
}

/**
 * Tooltip trigger mode — SSOT for the mobile flash-on-tap fix.
 *
 * On touch devices Recharts' default `trigger="hover"` responds to the
 * synthetic `pointerenter`/`pointerleave` pair a tap fires in rapid
 * succession, so the tooltip flashes for a split second and disappears
 * before the user can read it. Switching to `trigger="click"` opens the
 * tooltip on tap and keeps it visible until the user taps elsewhere.
 * Desktop keeps `hover` so mouse interaction is unaffected.
 */
export function useChartTooltipTrigger(): { trigger: 'hover' | 'click' } {
  const touchOnly = useIsTouchOnly();
  return { trigger: touchOnly ? 'click' : 'hover' };
}

/** Inline-row class set — tabular numerals keep columns aligned. */
export const TOOLTIP_VALUE_CLASSNAME = 'tabular-nums';

export interface ChartTooltipProps extends TooltipProps<number, string> {
  /**
   * Format the bold top-line label (usually an ISO bucket). Default: the
   * raw `label` value is rendered verbatim — pass a formatter to humanise
   * bucket timestamps.
   */
  labelFormatter?: (label: unknown) => React.ReactNode;
  /**
   * Format each series row. Return `null` to skip the row entirely.
   * Default: `${name}: ${value}`.
   */
  valueFormatter?: (
    value: number | string | null | undefined,
    name: string | undefined,
  ) => React.ReactNode;
  /**
   * When true, prepend a coloured swatch that matches the series stroke/fill.
   * Default: true — most callers want the swatch to disambiguate multi-series.
   */
  showSwatch?: boolean;
  /**
   * When true, append a summed "Total" row after the series list. Only the
   * numeric payload entries contribute. Useful for stacked bar charts
   * (Users trend: new + returning) where the composition is interesting
   * but the *sum* is the headline number.
   */
  showTotal?: boolean;
  /** Override the label text for the total row. Defaults to "Total". */
  totalLabel?: string;
}

/**
 * Default themed tooltip. Drop-in replacement for Recharts' built-in
 * `<Tooltip />` — pass via `content`:
 *
 *   <Tooltip content={<ChartTooltip />} />
 *
 * Reads `active`, `payload`, and `label` from the recharts-supplied props.
 */
export function ChartTooltip({
  active,
  payload,
  label,
  labelFormatter,
  valueFormatter,
  showSwatch = true,
  showTotal = false,
  totalLabel = 'Total',
}: ChartTooltipProps): React.ReactElement | null {
  if (!active || !payload || payload.length === 0) return null;

  const renderedLabel = labelFormatter ? labelFormatter(label) : (label as React.ReactNode);

  const total = showTotal
    ? payload.reduce<number>((acc, entry) => {
        const v = entry.value;
        return acc + (typeof v === 'number' && Number.isFinite(v) ? v : 0);
      }, 0)
    : 0;

  return (
    <div data-testid="chart-tooltip" className={TOOLTIP_CLASSNAME}>
      {renderedLabel !== undefined && renderedLabel !== null && renderedLabel !== '' && (
        <div className="mb-1 font-medium">{renderedLabel}</div>
      )}
      <ul className="space-y-0.5">
        {payload.map((entry, idx) => {
          const name = entry.name as string | undefined;
          const raw = entry.value as number | string | null | undefined;
          const rendered = valueFormatter
            ? valueFormatter(raw, name)
            : `${name ?? ''}${name ? ': ' : ''}${raw ?? '—'}`;
          if (rendered === null) return null;
          return (
            <li
              key={`${name ?? 'series'}-${idx}`}
              className="flex items-center gap-2"
            >
              {showSwatch && entry.color && (
                <span
                  aria-hidden
                  className="inline-block h-2 w-2 rounded-sm"
                  style={{ background: entry.color }}
                />
              )}
              <span className={TOOLTIP_VALUE_CLASSNAME}>{rendered}</span>
            </li>
          );
        })}
        {showTotal && payload.length > 1 && (
          <li
            data-testid="chart-tooltip-total"
            className="mt-1 flex items-center gap-2 border-t pt-1 font-medium"
          >
            {showSwatch && <span aria-hidden className="inline-block h-2 w-2" />}
            <span className={TOOLTIP_VALUE_CLASSNAME}>
              {totalLabel}: {total.toLocaleString()}
            </span>
          </li>
        )}
      </ul>
    </div>
  );
}
